import type { ReactNode } from 'react';

import { getLocale, type Locale } from '../../../i18n';
import { Absent, EstimateLabel } from '../../../chrome/measure';
import { count, share } from '../../../chrome/measure/format';
import { StatCard, StatGrid } from '../../../chrome/page';
import type { Territory } from '../api/schemas';
import { labellingOf } from './estimate';
import { tt } from './messages';

type Electorate = NonNullable<Territory['electorate']>;

const ABSENT = { value: <Absent reason="not_measured" />, estimate: null };

/** One electorate figure: absence and zero are different statements, so the
 *  raw value is compared with `null`/`undefined` and never tested for
 *  truthiness. Refused the same way `registered` is, on the electorate's own
 *  shared estimate fields — no figure here carries its own. */
function figureOf(
  electorate: Electorate | null,
  raw: (electorate: Electorate) => number | null | undefined,
  format: (value: number, locale: Locale) => string,
): { value: ReactNode; estimate: ReactNode } {
  if (electorate === null) return ABSENT;
  const labelling = labellingOf(electorate);
  const value = raw(electorate);
  if (value === null || value === undefined || labelling.kind === 'refused') return ABSENT;
  return {
    value: format(value, getLocale()),
    estimate:
      labelling.kind === 'estimate' ? (
        <EstimateLabel methodVersion={labelling.methodVersion} confidence={labelling.confidence} />
      ) : null,
  };
}

/** Three of the frame's four electorate figures; the fourth, registration
 *  movement, has no source yet (platform-design §17.10). The estimate label
 *  sits in the caption, which wraps, rather than beside the figure, which
 *  does not. */
export function ElectorateStat(props: {
  electorate: Territory['electorate'];
  note?: string;
}): ReactNode {
  const electorate = props.electorate ?? null;
  const registered = figureOf(electorate, (e) => e.registered, count);
  const sections = figureOf(electorate, (e) => e.sections, count);
  const turnout = figureOf(electorate, (e) => e.turnout, share);

  return (
    <div className="flex flex-col gap-2">
      <StatGrid>
        <StatCard
          label={tt('electorate.label')}
          value={registered.value}
          caption={
            electorate === null ? undefined : (
              <>
                {tt('electorate.caption', { locality: electorate.locality_name })}{' '}
                {registered.estimate}
              </>
            )
          }
        />
        <StatCard
          label={tt('electorate.sections.label')}
          value={sections.value}
          caption={sections.estimate ?? undefined}
        />
        <StatCard
          label={tt('electorate.turnout.label')}
          value={turnout.value}
          caption={turnout.estimate ?? undefined}
        />
      </StatGrid>
      {props.note === undefined ? null : (
        <p data-slot="electorate-note" className="text-caption text-muted-foreground">
          {props.note}
        </p>
      )}
    </div>
  );
}
